<template>
  <DashboardLayout :breadcrumbs="breadcrumbs" :hierarchy-params="{document: $route.params.documentId}" ref="dashboard">
    <div class="container is-fluid">
      <div class="level mo mb-7">
        <div class="level-left">
          <div class="control mr-10">
            <h1 class="title is-5">
              <b-tooltip :label="$t('docflow.signed')"
                         class="sign-ico"
                         type="is-light"
                         v-if="detail.pkcs7_info">
                <b-icon icon="check-decagram" size="is-small"></b-icon>
              </b-tooltip>
              {{ $t('docflow.fullDocTypes.outgoing') }}
              {{detail.registration_number}}
            </h1>
          </div>
          <div class="control mr-10">
            <b-dropdown aria-role="list">
              <a slot="trigger" role="button" class="button is-small">
                <span>{{ $t('tasks.actions') }}</span>
                <b-icon icon="chevron-down" size="is-small"></b-icon>
              </a>
              <b-dropdown-item @click.native="link(detail.file)" v-if="!detail.is_classified">
                <b-icon icon="download" size="is-small" class="mr-5"></b-icon>
                <span>{{ $t('interface.downloadDocument') }}</span>
              </b-dropdown-item>
              <b-dropdown-item @click.native="openCard">
                <b-icon icon="card-bulleted" size="is-small" class="mr-5"></b-icon>
                <span>{{ $t('interface.card') }}</span>
              </b-dropdown-item>
              <b-dropdown-item @click.native="getRegisterCard">
                <b-icon icon="file-document-outline" size="is-small" class="mr-5"></b-icon>
                <span>{{ $t('interface.registerCard') }}</span>
              </b-dropdown-item>
              <b-dropdown-item @click.native="remove" v-if="detail.state === 'new' && (isDocflowModerator || isAuthor)">
                <b-icon icon="trash-can" size="is-small" class="mr-5"></b-icon>
                <span>{{ $t('interface.remove') }}</span>
              </b-dropdown-item>
            </b-dropdown>
          </div>
          <div class="control">
            <a class="edit mr-10" @click="edit" v-if="editable || (detail.change_state === 'approved' && isSupervisor)">
              <b-icon icon="pencil" size="is-small" class="icon"></b-icon>
              {{ $t('interface.edit') }}
            </a>
            <a class="button is-success is-small mr-10"
               v-if="['rejected', 'new'].includes(detail.state) && isAuthor"
               @click="nextState">
              {{ $t('docflow.sendForAgreement') }}
            </a>
            <a class="button is-success is-small mr-10"
               v-if="detail.state === 'pending_plan_agreement' && detail.can_agree_plan"
               @click="nextState">
              {{ $t('docflow.agreePlan') }}
            </a>
            <template v-if="detail.state === 'pending_checkout' && detail.can_checkout">
              <a class="button is-success is-small mr-10"
                 @click="checkout">
                {{ $t('docflow.agree') }}
              </a>
              <a class="button is-danger is-small mr-10"
                 @click="rejectCheckout">
                {{ $t('interface.reject') }}
              </a>
            </template>
            <template v-if="detail.state === 'executive_checking' && isDocflowExecutiveOfficer">
              <a class="button is-success is-small mr-10"
                 @click="nextState">
                {{ $t('interface.checked') }}
              </a>
              <a class="button is-danger is-small mr-10"
                 @click="reject">
                {{ $t('interface.reject') }}
              </a>
            </template>
            <a class="button is-success is-small mr-10"
               v-if="detail.can_agree"
               @click="nextState">
              {{ $t('docflow.pend') }}
            </a>
            <a class="button is-success is-small mr-10"
               v-if="detail.can_approve"
               @click="nextState">
              {{ $t('docflow.approve') }}
            </a>
            <a class="button is-success is-small mr-10"
               v-if="detail.can_attach_registration_number"
               @click="nextState">
              {{ $t('docflow.attachRegistrationNumber') }}
            </a>
            <a class="button is-success is-small mr-10"
               v-if="detail.can_publish"
               @click="nextState">
              {{ $t('docflow.publish') }}
            </a>
            <a class="button is-success is-small mr-10"
               v-if="detail.can_register"
               @click="register">
              {{ $t('docflow.register') }}
            </a>
            <a class="button is-success is-small mr-10"
               v-if="detail.state === 'pending_closing' && isDocflowSupervisor && !detail.can_register"
               @click="nextState">
              {{ $t('docflow.close') }}
            </a>
            <a class="button is-danger is-small mr-10"
               v-if="(detail.state === 'pending_plan_agreement' && detail.can_agree_plan)
                      || (detail.state === 'agreed' && isApprover)
                      || (detail.state === 'pending_agreement' && isSupervisor)"
               @click="reject">
              {{ $t('interface.reject') }}
            </a>
            <a class="button is-warning is-small mr-10"
               v-if="(detail.state === 'pending_closing' && isSupervisor)
                      || (['pending_checkout', 'pending_agreement', 'agreed'].includes(detail.state) && isAuthor)
                      || (['pending_agreement', 'agreed'].includes(detail.state) && isDocflowAssistant)"
               @click="reject">
              {{ $t('interface.return') }}
            </a>
            <a @click="changeRequest = true" class="button is-small is-warning mr-10"
               v-if="canSendChangeRequest && (!detail.change_state || ['done', 'rejected'].includes(detail.change_state))">
              {{ $t('docflow.sendChangeRequest') }}
            </a>
            <a class="button is-danger is-small"
               v-if="!['cancelled', 'closed', 'new'].includes(detail.state) && isAuthor"
               @click="cancel">
              {{ $t('interface.cancel') }}
            </a>
          </div>
        </div>
      </div>
      <div class="flags mb-10">
        <b-tag v-if="detail.is_classified">
          {{ $t(`docflow.is_classified`) }}
        </b-tag>
        <b-tag v-if="detail.is_pending_cancellation">
          {{ $t(`docflow.is_pending_cancellation`) }}
        </b-tag>
        <b-tag v-if="detail.is_informational">
          {{ $t(`docflow.is_informational`) }}
        </b-tag>
      </div>
      <b-tabs @change="tabChange" v-model="tabIndex">
        <b-tab-item :label="$t('interface.information')">
          <div class="columns">
            <div class="column is-7">
              <div class="document description">
                <table>
                  <tr>
                    <td><strong>{{ $t('docflow.author') }}</strong></td>
                    <td>
                      <a @click="userDetail(detail.author.id)">{{ detail.author.initials }}</a>
                    </td>
                    <td><strong>{{ $t('docflow.fields.type') }}</strong></td>
                    <td>{{ detail.type__name }}</td>
                  </tr>
                  <tr>
                    <td><strong>{{ $t('tasks.currentState') }}</strong></td>
                    <td>
                      <b-tag :type="`is-${stateColors[detail.state]}`">
                        {{ $t(`workflow.states.${detail.state}`) }}
                      </b-tag>
                    </td>
                    <td><strong>{{ $t('tasks.termDate') }}</strong></td>
                    <td>
                      <template v-if="!detail.is_informational">
                        {{ detail.term_date | localedDate }}
                      </template>
                      <template v-else>&ndash;</template>
                    </td>
                  </tr>
                  <tr>
                    <td><strong>{{ $t('docflow.approver') }}</strong></td>
                    <td>
                      {{ detail.approver ? detail.approver.initials : '-' }}
                    </td>
                    <td><strong>{{ $t('docflow.maintainer') }}</strong></td>
                    <td>
                      {{ detail.maintainer ? detail.maintainer.initials : '-' }}
                    </td>
                  </tr>
                  <tr>
                    <td><strong>{{ $t('docflow.fields.registered_date') }}</strong></td>
                    <td>
                      <span v-if="detail.registered_date">{{ detail.registered_date | localedDate }}</span>
                      <span v-else>-</span>
                    </td>
                    <td><strong>{{ $t('docflow.fields.supervisor') }}</strong></td>
                    <td>{{ detail.supervisor ? detail.supervisor.initials : '&ndash;' }}</td>
                  </tr>
                  <tr>
                    <td><strong>{{ $t('docflow.fields.lawyer') }}</strong></td>
                    <td>{{ detail.lawyer ? detail.lawyer.initials : '&ndash;' }}</td>
                    <td><strong>{{ $t('docflow.fields.spotter') }}</strong></td>
                    <td>{{ detail.spotter ? detail.spotter.initials : '&ndash;' }}</td>
                  </tr>
                  <tr>
                    <template v-if="detail.parent">
                      <td><strong>{{ $t('docflow.parentDocument') }}</strong></td>
                      <td>
                        <a @click="documentDetail(detail.parent.id, detail.parent.category)">{{
                          detail.parent.registration_number }}</a>
                      </td>
                    </template>
                  </tr>
                  <template v-if="detail.applicant">
                    <tr>
                      <td><strong>{{ $t('docflow.fields.applicantType') }}</strong></td>
                      <td>
                        {{ $t(`docflow.applicantTypes.${detail.applicant.applicant_type}`) }}
                      </td>
                      <td><strong>{{ $t('docflow.fields.applicant_name') }}</strong></td>
                      <td>{{ detail.applicant.applicant_name }}</td>
                    </tr>
                    <tr>
                      <td><strong>{{ $t('docflow.fields.applicant_passport_number') }}</strong></td>
                      <td>
                        {{ detail.applicant.applicant_passport_number }}
                      </td>
                      <td><strong>{{ $t('docflow.fields.applicant_region') }}</strong></td>
                      <td>{{ detail.applicant.applicant_region__name }}</td>
                    </tr>
                    <tr>
                      <td><strong>{{ $t('docflow.fields.applicant_area') }}</strong></td>
                      <td>
                        {{ detail.applicant.applicant_area__name }}
                      </td>
                      <td><strong>{{ $t('docflow.fields.applicant_address') }}</strong></td>
                      <td>{{ detail.applicant.applicant_address }}</td>
                    </tr>
                    <tr>
                      <td><strong>{{ $t('docflow.fields.applicant_work_phone') }}</strong></td>
                      <td>
                        {{ detail.applicant.applicant_work_phone }}
                      </td>
                      <td><strong>{{ $t('docflow.fields.applicant_mobile_phone') }}</strong></td>
                      <td>{{ detail.applicant.applicant_mobile_phone }}</td>
                    </tr>
                  </template>
                </table>
                <div class="mb-30">
                  <strong>{{ $t('interface.description') }}</strong>
                  <p>{{ detail.description }}</p>
                </div>
                <Correspondents :items="detail.correspondents"
                                :editable="editable"
                                :can-add-external="false"
                                :title="$t('docflow.correspondents')"
                                @add="addCorrespondent"
                                @remove="removeCorrespondent">
                </Correspondents>
                <div class="workflow mb-20 mt-20">
                  <h3 class="title is-6 mb-0">{{ $t('docflow.workflow.title') }}</h3>
                  <b-table :data="detail.assignments" class="maintainers-table">
                    <template slot-scope="props">
                      <b-table-column :label="$t('docflow.workflow.name')" field="name" sortable>
                        <a @click.prevent="assignmentDetail(props.row.id)">{{ props.row.name }}</a>
                      </b-table-column>
                      <b-table-column :label="$t('docflow.workflow.term_date')" width="120">
                        {{ props.row.term_date | localedDate }}
                      </b-table-column>
                    </template>
                    <template slot="empty">
                      <section class="section">
                        <div class="content is-fluid has-text-grey has-text-centered">
                          <p>
                            <span class="has-text-grey-light">{{ $t('docflow.assignmentsTip') }}</span>
                          </p>
                        </div>
                      </section>
                    </template>
                  </b-table>
                </div>
                <!--<Attachments class="mt-15"
                             size="large"
                             @file-upload="uploadFile"
                             @remove-file="removeFile"
                             :editable="isAuthor || isDocflowController"
                             :upload="upload"
                             :title="$t('docflow.attachments')"
                             :data="detail.attachments">
                </Attachments>-->
                <!--<Comments :comments="detail.comments"
                          @create="createComment"
                          @edit="editComment"
                          @remove="removeComment">
                </Comments>-->
              </div>
            </div>
            <div class="column is-5">
              <h3 class="title is-6 mb-15">{{ $t('docflow.progress') }}</h3>
              <Timeline :data="timeline" class="mb-5"/>
              <ChangeRequestControls :data="detail.change_request_states"
                                     @change="loadDetail"
                                     v-if="isSupervisor"/>
            </div>
          </div>
        </b-tab-item>
        <b-tab-item :label="$t('docflow.documentPlan')">
          <DocflowPlan @refresh="loadDetail"></DocflowPlan>
        </b-tab-item>
        <b-tab-item :label="$t('docflow.checkouts')">
          <DocflowCheckouts @refresh="loadDetail"></DocflowCheckouts>
        </b-tab-item>
        <b-tab-item :label="$t('docflow.planControl')" v-if="isReportsAvailable">
          <PlanControl></PlanControl>
        </b-tab-item>
        <b-tab-item :label="$t('docflow.assignmentsControl')" v-if="isReportsAvailable">
          <AssignmentsControl></AssignmentsControl>
        </b-tab-item>
        <b-tab-item :label="$t('docflow.relatedDocumentsControl')" v-if="isReportsAvailable">
          <RelatedDocumentsControl></RelatedDocumentsControl>
        </b-tab-item>
        <b-tab-item :label="$t('interface.preview')">
          <DocumentPreview></DocumentPreview>
        </b-tab-item>
      </b-tabs>
    </div>
    <b-modal class="dialog" :class="{big: noteModalMode === 'closing'}" :active.sync="isNoteModalActive" has-modal-card>
      <div class="modal-card" style="width: auto">
        <section class="modal-card-body is-titleless">
          <template v-if="noteModalMode === 'publish'">
            <form-input mask="9a-999"
                        class="mb-10"
                        :label="$t('docflow.fields.registration_number')"
                        v-model="note">
            </form-input>
            <b-field class="file">
              <b-upload v-model="publish_file">
                <a class="button is-warning">
                  <span>{{ $t('docflow.fields.file') }}</span>
                </a>
              </b-upload>
              <span class="file-name">
                <template v-if="publish_file">{{ publish_file.name }}</template>
                <template v-else>{{ $t('interface.noFileSelected') }}</template>
              </span>
            </b-field>
          </template>
          <template v-else>
            <form-select :label="$t('docflow.decisionType')" v-model="decisionType" v-if="noteModalMode === 'closing'">
              <option v-for="item in decisionTypes" :value="item.id">{{item.name}}</option>
            </form-select>
            <textarea class="input mt-10"
                      ref="noteInput"
                      v-model="note"
                      :placeholder="$t('interface.inputNote')">
            </textarea>
          </template>
        </section>
        <footer class="modal-card-foot">
          <button class="button is-primary" @click="noteSubmit">OK</button>
          <button class="button is-danger" type="button" @click="isNoteModalActive = false">
            {{ $t('interface.cancel') }}
          </button>
        </footer>
      </div>
    </b-modal>
    <DocumentChangeRequest :active.sync="changeRequest" @change="loadDetail"></DocumentChangeRequest>
    <Sign :data="detail.sign_data" ref="eimzo" @output="onSign"></Sign>
  </DashboardLayout>
</template>

<script>
  import {mapState, mapGetters} from 'vuex'
  import {stateColors} from '@/colorTypes'
  import DashboardLayout from '@/components/DashboardLayout'
  import DocumentPreview from '@/components/DocumentPreview'
  import DocflowCheckouts from '@/components/DocflowCheckouts'
  import Attachments from '@/components/Attachments'
  import Correspondents from '@/components/Correspondents'
  import Comments from '@/components/Comments'
  import Sign from '@/components/Sign'
  import DocflowPlan from '@/components/DocflowPlan'
  import Timeline from '@/components/Timeline'
  import PlanControl from '@/components/PlanControl'
  import AssignmentsControl from '@/components/AssignmentsControl'
  import RelatedDocumentsControl from '@/components/RelatedDocumentsControl'
  import DocumentChangeRequest from '@/components/DocumentChangeRequest'
  import ChangeRequestControls from '@/components/ChangeRequestControls'
  import {timelineItem} from '@/constants'
  import {userDetail, documentDetail, assignmentDetail, outputFile, s2ab} from '@/utils'

  const sections = [
    'detail',
    'plan',
    'checkouts',
    'planControl',
    'assignmentsControl',
    'relatedDocumentsControl',
    'documentPreview'
  ]

  export default {
    components: {
      DashboardLayout,
      Correspondents,
      Attachments,
      DocflowPlan,
      Comments,
      DocflowCheckouts,
      Timeline,
      PlanControl,
      AssignmentsControl,
      RelatedDocumentsControl,
      DocumentChangeRequest,
      ChangeRequestControls,
      DocumentPreview,
      Sign
    },
    data() {
      return {
        stateColors,
        isNoteModalActive: false,
        noteModalMode: '',
        note: '',
        tabIndex: 0,
        publish_file: null,
        decisionTypes: [],
        decisionType: null,
        changeRequest: false
      }
    },
    methods: {
      documentDetail,
      userDetail,
      assignmentDetail,
      loadDetail() {
        this.$store.dispatch('Docflow/getDetail', {
          documentId: this.$route.params.documentId,
          category: 'outgoing'
        })
        this.$refs.dashboard.loadHierarchy()
      },
      edit() {
        this.$router.push({
          name: 'editOutgoingDocflow',
          params: {
            documentId: this.$route.params.documentId
          }
        })
      },
      nextState() {
        if (this.detail.state === 'pending_closing') {
          this.$store.dispatch('Docflow/getCloseReport').then(data => {
            this.note = data.text
            this.decisionTypes = data.decision_types
            this.decisionType = data.decision_types[0].id
            this.noteModalMode = 'closing'
            this.isNoteModalActive = true
          })
        } else if (this.detail.state === 'approved') {
          this.isNoteModalActive = true
          this.noteModalMode = 'publish'
          this.note = ' '
        } else if (this.detail.is_sign_required) {
          this.$refs.eimzo.sign()
        } else {
          this.$store.dispatch('Docflow/nextState').then(this.loadDetail)
        }
      },
      onSign(pkcs7) {
        this.$store.dispatch('Docflow/nextState', {pkcs7}).then(this.loadDetail)
      },
      checkout() {
        this.$store.dispatch('Docflow/checkout').then(() => this.loadDetail())
      },
      rejectCheckout() {
        this.isNoteModalActive = true
        this.noteModalMode = 'rejectCheckout'
      },
      register() {
        this.$router.push({
          name: 'docflowRegister',
          params: {
            documentId: this.detail.id
          }
        })
      },
      remove() {
        this.$dialog.confirm({
          title: this.$t('interface.confirm'),
          message: this.$t('interface.areYouSureRemove'),
          confirmText: this.$t('interface.remove'),
          cancelText: this.$t('interface.cancel'),
          type: 'is-danger',
          hasIcon: true,
          onConfirm: () => this.$store.dispatch('Docflow/remove', this.$route.params.documentId).then(() => {
            this.$router.push({name: 'docflowOutgoing'})
          })
        })
      },
      cancel() {
        this.isNoteModalActive = true
        this.noteModalMode = 'cancel'
      },
      reject() {
        this.isNoteModalActive = true
        this.noteModalMode = 'reject'
      },
      noteSubmit() {
        if (this.note.trim().length === 0 && this.noteModalMode !== 'closing') {
          this.$toast.open(this.$t('interface.allRequired'))
          return false
        }
        let note = this.note !== '' ? this.note : null
        if (this.noteModalMode === 'publish') {
          this.$store.dispatch('Docflow/attachRegistrationNumber', {
            registration_number: note,
            file: this.publish_file
          }).then(() => this.loadDetail())
        } else if (this.noteModalMode === 'rejectCheckout') {
          this.$store.dispatch('Docflow/rejectCheckout', note).then(() => this.loadDetail())
        } else if (this.noteModalMode === 'closing') {
          this.$store.dispatch('Docflow/nextState', {
            note,
            decision_type: this.decisionType
          }).then(this.loadDetail)
        } else if (this.noteModalMode === 'cancel') {
          this.$store.dispatch('Docflow/cancel', {note}).then(this.loadDetail)
        } else {
          this.$store.dispatch('Docflow/reject', note).then(() => this.loadDetail())
        }
        this.isNoteModalActive = false
        this.note = ''
      },
      addCorrespondent(correspondent) {
        if (correspondent.type !== 'new') {
          let data = {type: correspondent.type}
          data[correspondent.type] = correspondent.id
          this.$store.dispatch("Docflow/addCorrespondent", data)
        } else {
          this.$store.dispatch("Docflow/addCorrespondent", correspondent)
        }
      },
      removeCorrespondent(correspondentId) {
        this.$store.dispatch("Docflow/removeCorrespondent", correspondentId)
      },
      uploadFile({file, type}) {
        this.$store.dispatch('Docflow/uploadFile', {file, type})
      },
      createComment(text) {
        this.$store.dispatch('Docflow/createComment', text)
      },
      editComment(comment) {
        this.$store.dispatch('Docflow/updateComment', comment)
      },
      removeComment(id) {
        this.$store.dispatch('Docflow/removeComment', id)
      },
      tabChange(index) {
        this.$router.push({
          name: 'docflowOutgoingDetail',
          params: {
            documentId: this.detail.id,
            section: sections[index]
          }
        })
      },
      link(link) {
        let win = window.open(link, '_blank')
        win.focus()
      },
      getRegisterCard() {
        this.$axios.get(`docflow/direct/${this.detail.hash}/?export=true`).then(response => {
          let data = new Blob([s2ab(atob(response.data))], {type: ''})
          outputFile('register_card.doc', data)
        })
      },
      openCard() {
        let routeData = this.$router.resolve({
          name: 'directDocument', params: {
            hash: this.detail.hash
          }
        })
        window.open(routeData.href, '_blank')
      }
    },
    mounted() {
      this.loadDetail()
      if (this.$route.params.section) this.tabIndex = sections.indexOf(this.$route.params.section)
    },
    computed: {
      ...mapState('Docflow', ['detail', 'upload']),
      ...mapState('Account', ['account']),
      ...mapGetters('Account', [
        'isDocflowManager',
        'isDocflowApprover',
        'isDocflowController',
        'isDocflowSupervisor',
        'isDocflowModerator',
        'isDocflowAssistant',
        'isDocflowExecutiveOfficer',
        'isSuperUser'
      ]),
      editable() {
        return ['new', 'rejected'].includes(this.detail.state) && (this.isMaintainer || this.isAuthor)
      },
      isAuthor() {
        return (this.detail.author && this.detail.author.id === this.account.employee.id) || this.isSuperUser
      },
      isMaintainer() {
        return (this.detail.maintainer && this.detail.maintainer.id === this.account.employee.id) || this.isSuperUser
      },
      isApprover() {
        return (this.detail.approver && this.detail.approver.id === this.account.employee.id) || this.isSuperUser
      },
      isSupervisor() {
        return (this.detail.supervisor && this.account.employee.id === this.detail.supervisor.id)
          || this.isDocflowSupervisor || this.isSuperUser
      },
      isReportsAvailable() {
        return ['published', 'pending_closing', 'closed', 'in_process'].includes(this.detail.state)
      },
      isCheckoutUser() {
        return this.detail.checkouts ?
          this.detail.checkouts.filter(c => c.employee === this.account.employee.id).length > 0 : false
      },
      isCorrespondent() {
        return this.detail.correspondents ?
          this.detail.correspondents.filter(c => c.employee === this.account.employee.id).length > 0 : false
      },
      canSendChangeRequest() {
        return ['approved', 'in_process', 'registration_number_attached'].includes(this.detail.state)
          && (this.isAuthor || this.isResolutionAuthor || this.isResolutionApprover || this.isCheckoutUser || this.isCorrespondent)
      },
      timeline() {
        let changeStates = this.detail.change_request_states.map(state => {
          let res = {
            ...timelineItem,
            date: state.created_at,
            title: this.$t(`workflow.changeStates.${state.state}`),
            note: `${state.author ? state.author.initials : ''}${state.note ? ` – ${state.note}` : ''}`,
          }
          if (state.file !== null) {
            res.attachments = [{
              extension: 'doc',
              file: state.file,
              name: this.$t('docflow.changeRequest.file'),
              created_at: state.created_at,
              removable: false
            }]
          }
          return res
        });
        let documentStates = this.detail.states.map(state => ({
          ...timelineItem,
          date: state.created_at,
          isSigned: state.is_signed,
          title: this.$t(`workflow.states.${state.state}`),
          note: `${state.author ? state.author.initials : ''}${state.note ? ` – ${state.note}` : ''}`,
        }))
        return [...changeStates, ...documentStates].sort((a, b) => new Date(a.date) - new Date(b.date))
      },
      breadcrumbs() {
        return [
          {name: 'docflow'},
          {name: 'docflowOutgoing', path: 'docflowOutgoing'},
          {name: 'docflowDetail'}
        ]
      }
    }
  }
</script>

<style lang="scss" scoped>
  .dialog {
    &.big {
      .modal-card {
        width: 800px !important;
      }

      .input {
        resize: vertical;
        min-height: 300px;
      }
    }

    .input {
      resize: vertical;
      min-height: 80px;
    }
  }

  .edit {
    font-size: 13px !important;

    .icon {
      margin-right: 2px;
    }
  }

  .document.description {
    color: #666;

    table {
      width: 100%;

      td {
        padding-bottom: 10px;
      }

      tr > td:first-child,
      tr > td:nth-child(3) {
        width: 140px;
      }
    }

    & > div {
      margin-top: 10px;

      & > p {
        margin-top: 4px;
      }
    }
  }
</style>
